import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "../css/signupFlow.css";
import "../css/loginPage.css";
import { useTheme } from "../hooks/useTheme";
import host from "../global";
import {
  EVENTS,
  track,
  identifyUser,
  setCompany,
  setInternal,
  isInternalEmail,
  captureAttribution,
  getAttribution,
  createFieldTracker,
} from "../utils/analytics";
import getCountryHint from "../utils/countryHint";
import { loadSignupTracking, trackSignupConversion } from "../utils/adTracking";
import {
  FREE_INTERVIEWS,
  FREE_CV_SCREENINGS,
  TERMS_URL,
  PRIVACY_URL,
  SALES_EMAIL,
  PLATFORM_ADDRESS,
  LINK_EMAIL_SUBJECT,
  EXAMPLE_ROWS,
  isHandheld,
  adNote,
  formatUntil,
  daysAgo,
  Arrow,
  Logo,
  Check,
  Agreement,
  LaptopPicture,
  NoteBox,
  StatusScreen,
  PhoneLanding,
} from "../components/signup/SignupParts";

// FastAPI returns `detail` as a string for HTTPException but as an array of
// objects ({type, loc, msg, ...}) for 422 validation errors. Rendering that
// array/object in JSX crashes React (error #31), so always reduce to a string.
const getErrorMessage = (err, fallback) => {
  const detail = err.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((d) => d?.msg).filter(Boolean).join(", ") || fallback;
  }
  return fallback;
};

// The signup code is six digits (OnboardingService.CODE_LENGTH).
// The backend's codes for an address that is not a work email, and the
// reason signup_failed gives for each
const BLOCKED_EMAIL_REASONS = {
  PERSONAL_EMAIL_NOT_ALLOWED: "personal_email",
  DISPOSABLE_EMAIL_NOT_ALLOWED: "temporary_email",
};

const CODE_LENGTH = 6;
const RESEND_SECONDS = 45;

// What survives a refresh between the steps, in this tab only
const STORE = {
  stage: "signupStage",
  email: "signupEmail",
  token: "signupToken",
  sentFrom: "signupSentFrom",
  linkExpiresAt: "signupLinkExpiresAt",
};
const clearSignupStore = () => Object.values(STORE).forEach((k) => sessionStorage.removeItem(k));

// "+919876543210" -> "India". Falls back to the ISO code, or "" when unknown.
const countryFromPhone = (phone) => {
  const code = phone ? parsePhoneNumber(phone)?.country : undefined;
  if (!code) return "";
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(code) || code;
  } catch {
    return code;
  }
};

// True when the access token carries a Cognito group, i.e. the account has an
// organization and a role. An account from the old signup flow that verified
// but never finished has none.
const hasRole = (token) => {
  try {
    return (jwtDecode(token)["cognito:groups"] || []).length > 0;
  } catch {
    return false;
  }
};

// A valid session that already has a role: someone set up, who belongs in the app
const signedInWithRole = () => {
  const token = sessionStorage.getItem("accessToken");
  if (!token) return false;
  try {
    const { exp } = jwtDecode(token);
    return Boolean(exp && exp * 1000 > Date.now()) && hasRole(token);
  } catch {
    return false;
  }
};

const FEATURES = [
  {
    icon: <img src="/loginpage-1.png" alt="Interview Icon" className="w-6 h-6" />,
    title: "Proctored AI Video Interviews",
    description: "Screen candidates faster with AI-led video interviews.",
  },
  {
    icon: <img src="/loginpage-2.png" alt="Interview Icon" className="w-6 h-6" />,
    title: "Automated Interview Scheduling",
    description: "Auto-handle scheduling, reminders, and follow-ups.",
  },
  {
    icon: <img src="/loginpage-3.png" alt="Interview Icon" className="w-6 h-6" />,
    title: "Smart Resume Screening",
    description: (
      <>
        AI understands, scores, and shortlists
        <br />
        resumes beyond keywords.
      </>
    ),
  },
  {
    icon: <img src="/loginpage-4.png" alt="Interview Icon" className="w-6 h-6" />,
    title: "AI-Generated Job Descriptions",
    description: "Create role-ready JDs in seconds using AI.",
  },
  {
    icon: <img src="/loginpage-5.png" alt="Interview Icon" className="w-6 h-6" />,
    title: "Bias-Reduced Hiring",
    description: "Make fair, data-driven hiring decisions, no guesswork.",
  },
  {
    icon: <img src="/loginpage-6.png" alt="Interview Icon" className="w-6 h-6" />,
    title: "Overall Insights & Candidate Scores",
    description: "Get clear candidate scores, rankings, and hiring recommendations.",
  },
];

// The sign-in screens' page: feature slider on the left, the form on the right.
// Its own component so the slider only runs while a sign-in screen is shown.
const SignInLayout = ({ children }) => {
  // Sets <html class="light|dark">. App.css's `.light` autofill rule is what keeps
  // autofilled email/password text dark here; without it the candidate pages'
  // global rule (phoneNumberStyle.css) paints it whitesmoke on the white inputs.
  useTheme();
  const [currentFeature, setCurrentFeature] = useState(0);
  const intervalRef = useRef(null);
  const [username, setUsername] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const storedUsername = sessionStorage.getItem("username") || localStorage.getItem("username");
    if (storedUsername) {
      setUsername(storedUsername + "!");
    }
  }, []);

  const startSlider = () => {
    intervalRef.current = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % FEATURES.length);
    }, 3000);
  };
  const stopSlider = () => {
    clearInterval(intervalRef.current);
  };
  useEffect(() => {
    startSlider();
    return () => clearInterval(intervalRef.current);
  }, []);
  const handleMouseEnter = () => {
    stopSlider();
  };
  const handleMouseLeave = () => {
    startSlider();
  };
  const handleDotClick = (index) => {
    stopSlider();
    setCurrentFeature(index);
    startSlider();
  };

  return (
    <div
      // Phones scroll instead: a clipped page can hide the form's buttons.
      className="min-h-screen lg:h-screen w-full flex lg:overflow-hidden"
      style={{
        backgroundImage: `url("${isMobile ? '/login-screen-mobile-bg.png' : '/white-pink_bg_img.png'}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="hidden lg:block w-1/2 relative">
        <div className="absolute inset-0 z-0 m-4" />
        <div className="absolute inset-0 z-20 p-12 flex flex-col justify-between h-full pointer-events-none items-center">
          <div className="pointer-events-auto w-full max-w-2xl ml-28">
            <img
              src="/genie-demo-logo-black.png"
              alt="GenieHire.ai"
              className="w-64"
            />
          </div>

          <div className="w-full max-w-2xl">
            <div className="justify-around pointer-events-auto">
              <div className="-mb-6 ml-16">
                <h1 className="text-5xl xl:text-6xl text-left font-bold leading-tight bg-gradient-to-r from-[#FF60B6] to-[#2960FD] bg-clip-text text-transparent pb-2">
                  <span className="">Precision Hiring,</span><br />
                  <span className="">Accelerated</span>
                </h1>
                <span className="font-mediumtext-[#333333] text-xl italic">Where AI Drives Every Hire</span>
                <p className="text-[#4D4D4D] mt-10 mb-6 text-lg max-w-md leading-relaxed font-medium">
                  Here’s What Awaits You
                </p>
              </div>
              <div className="slideshow-container relative w-full" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                <div className="slideshow-wrapper flex items-center justify-center overflow-hidden bg-#ededed">
                  {FEATURES.map((feature, index) => {
                    const diff = index - currentFeature;
                    const isActive = index === currentFeature;
                    const isPrev = diff === -1 || (currentFeature === 0 && index === FEATURES.length - 1);
                    const isNext = diff === 1 || (currentFeature === FEATURES.length - 1 && index === 0);

                    let positionClass = "opacity-0 scale-90 translate-x-full pointer-events-none";
                    if (isActive) positionClass = "opacity-100 scale-100 translate-x-0 z-10";
                    else if (isPrev) positionClass = "opacity-40 scale-90 -translate-x-full blur-[2px]";
                    else if (isNext) positionClass = "opacity-40 scale-90 translate-x-full blur-[2px]";

                    return (
                      <div
                        key={index}
                        className={`slideshow-card absolute transition-all duration-300 ease-in-out p-6 rounded-3xl bg-[#fafafa] ${positionClass}`}
                        style={{ width: '80%', maxWidth: '700px' }}
                      >
                        <div className="flex items-start space-x-4 h-24 overflow-hidden p-4">
                          <div className="text-2xl mt-1 flex-shrink-0">
                            {feature.icon}
                          </div>

                          <div className="flex flex-col">
                            <h3 className="text-xl font-semibold text-[#404040] line-clamp-1">
                              {feature.title}
                            </h3>

                            <p className="text-[#808080] text-md leading-tight line-clamp-3">
                              {feature.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-center items-center space-x-2">
                  {FEATURES.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => handleDotClick(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentFeature
                          ? "active-dot scale-125 bg-slate-50"
                          : "bg-gray-600/50 hover:bg-gray-500"
                        }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="w-full max-w-2xl">
            <div className="flex flex-wrap justify-start gap-x-4 gap-y-1 text-[12px] text-[#707070] pointer-events-auto -mb-6" />

            <div className="px-8 absolute bottom-8 left-0 w-full flex justify-start gap-x-4">
              <div className="text-xs text-[#707070] pointer-events-auto">
                Copyright© 2025 NSquare Innovators Inc. All rights reserved.
              </div>
              <div className="text-xs text-[#707070] pointer-events-auto">
                <a href="/grievance-form" className="hover:text-slate-600 transition-colors">Help and Support</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 lg:h-full relative">
        <div
          className="absolute inset-0 z-0 m-4 hidden lg:block"
          style={{
            overflow: 'hidden',
            // CSS Masking
            maskImage: 'url("/Login_page_image_layer.png")',
            WebkitMaskImage: 'url("/Login_page_image_layer.png")',
            maskSize: '100% 100%',
            WebkitMaskSize: '100% 100%',
            maskPosition: 'center',
            WebkitMaskPosition: 'center',
            maskRepeat: 'no-repeat',
            WebkitMaskRepeat: 'no-repeat',
            transform: "scaleX(-1)",
          }}
        >
          <div className="absolute inset-0 bg-black/40 z-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/100 via-white/100 to-white/100 z-10" />

          {/* Main Background Image - Flipped */}
          <img
            src="/Login_page_image.jpg"
            alt="Background"
            className="w-full h-full object-cover object-top transform -scale-x-100"
          />
        </div>
        {/* lg:pl-20 keeps the form clear of the white shape's slanted left
            edge, which is wide relative to the half-screen at 1024-1279px. */}
        <div className="min-h-screen lg:min-h-0 lg:h-full flex flex-col justify-center p-4 sm:p-8 lg:pl-20 xl:pl-8 relative z-10" >
          {/* On phones this box is the white card, so it grows with the form
              instead of being a fixed rectangle the logo and links spill out of. */}
          <div className="w-full max-w-md mx-auto bg-white rounded-3xl py-8 lg:py-0 lg:bg-transparent lg:rounded-none">
            <div className="lg:mb-8 space-y-4">
              <div className="text-left space-y-6 lg:space-y-8 lg:pl-6">
                <div className="lg:hidden px-4">
                  <img
                    src="/genie-demo-logo-black.png"
                    alt="GenieHire.ai"
                    className="w-48 sm:w-64"
                  />
                </div>
                <div className="space-y-2 px-4">
                  <h2 className="text-xl sm:text-2xl font-semi-bold mb-1 text-[#525252]">
                    Welcome Back,
                  </h2>
                  <h2 className="text-4xl sm:text-5xl font-bold mb-1 text-transparent bg-clip-text bg-gradient-to-r from-[#2960FD] to-[#FF60B6] truncate">
                    <span title={username || "User!"}>
                      {username || "User!"}
                    </span>
                  </h2>
                </div>
                <div className="pb-2 px-4">
                  <p className="text-md sm:text-lg text-[#667085]">
                    Sign in to your GenieHire Portal
                  </p>
                </div>
              </div>
              <div className="flex justify-start w-full [&>form]:w-full">
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// `initialMode` lets /signup render this same page as the onboarding wizard.
// Reusing the component rather than copying it keeps the "back to sign in"
// path a mode change.
function Login({ initialMode = "login" }) {
  const navigate = useNavigate();

  // "login" -> "newPassword" (first login after account creation) -> "forgotRequest" -> "forgotConfirm"
  // Onboarding wizard: "signupStart" -> "signupVerify" -> "signupDetails"
  // On a phone or tablet: "signupStart" -> "signupLinkSent", then the emailed
  // link opens "signupLinkCheck" -> "signupDetails" on a laptop, or
  // "signupLinkOnPhone" / "signupLinkExpired" / "signupLinkUsed" / "signupLinkInvalid".
  // Turned away: "signupAccountExists", "signupJoin" (the company has an account).
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // ---- onboarding wizard ----
  // One box per digit. Held as an array so clearing a box in the middle leaves
  // a gap rather than shifting every digit after it left.
  const [codeDigits, setCodeDigits] = useState(() => Array(CODE_LENGTH).fill(""));
  const codeRefs = useRef([]);
  const signupCode = codeDigits.join("");
  const [codeSentTo, setCodeSentTo] = useState("");
  const [sentFrom, setSentFrom] = useState("");
  const [resendIn, setResendIn] = useState(0);
  // From step 2; step 3 creates the account with it. Empty on step 3 means an
  // account from the old flow finishing with its access token instead.
  const [signupToken, setSignupToken] = useState("");
  const [details, setDetails] = useState({ name: "", phoneNumber: "" });
  const [phoneCountry, setPhoneCountry] = useState("IN");
  const [note] = useState(adNote);
  const [handheld] = useState(isHandheld);
  // Phones and tablets: when the emailed link stops working
  const [linkExpiresAt, setLinkExpiresAt] = useState("");
  // "Ask to join": {status: "ask" | "sent" | "waiting" | "reminded", domain, requestedAt, nextReminderAt}
  const [join, setJoin] = useState(null);
  const linkOpened = useRef(false);
  const emailRef = useRef(null);
  const finishingOldAccount = mode === "signupDetails" && !signupToken;

  // Same IP lookup as the company profile: start the phone field on the
  // visitor's country, India if the lookup fails.
  useEffect(() => {
    if (mode !== "signupDetails") return;
    const detectCountry = async () => {
      try {
        const { data } = await axios.get("https://ipwho.is/", { timeout: 3000 });
        const code = data.country_code?.toUpperCase();
        if (code) setPhoneCountry(code);
      } catch (e) {
        console.warn("IP geolocation failed, using fallback");
      }
    };
    detectCountry();
  }, [mode]);

  // The signup form view is counted once each time this page opens on the
  // signup form, including coming over from the login form (goToSignup).
  const signupViewTracked = useRef(false);

  // One field tracker per signup form, kept in a ref so re-renders keep what
  // has been seen. The submitted flags stop leaving afterwards from counting
  // as abandoning the form.
  const credentialFields = useRef(createFieldTracker("signup_credentials"));
  const detailFields = useRef(createFieldTracker("signup_details"));
  const credentialsSubmitted = useRef(false);
  const detailsSubmitted = useRef(false);

  // Amplify's <Authenticator> used to do this invisibly -- resume an existing
  // session instead of showing the login form again. Our own login owns that
  // job now: if the accessToken cookie from a previous login is still valid,
  // skip straight to role-based redirect instead of asking to sign in again.
  useEffect(() => {
    if (initialMode !== "login") return;
    const token = sessionStorage.getItem("accessToken");
    if (!token) return;
    try {
      const { exp } = jwtDecode(token);
      if (exp && exp * 1000 > Date.now()) {
        navigate("/role-redirect", { replace: true });
      } else {
        sessionStorage.removeItem("accessToken");
      }
    } catch {
      sessionStorage.removeItem("accessToken");
    }
  }, [navigate, initialMode]);

  // Meta Pixel and Clarity, on /signup only (adTracking.js)
  useEffect(() => {
    if (initialMode === "signupStart") loadSignupTracking();
  }, [initialMode]);

  // Top of the funnel: everyone who opens the signup form, including those
  // who leave without typing anything. The login form is not counted.
  useEffect(() => {
    if (mode !== "signupStart" || signupViewTracked.current) return;
    signupViewTracked.current = true;
    track(EVENTS.SIGNUP_PAGE_VIEWED);
    // Keep the campaign this visit came from; it is sent with signed_up
    captureAttribution();
    // Phones send their country with step 1 (see handleSignupStart); looked
    // up now so submitting doesn't wait for it
    if (handheld) getCountryHint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // signup_form_abandoned: leaving step 1 or step 3 without submitting, by
  // closing the tab, switching away on mobile (where beforeunload is
  // unreliable, visibilitychange is not) or going back to sign in.
  useEffect(() => {
    const forms = {
      signupStart: [credentialFields, credentialsSubmitted],
      signupDetails: [detailFields, detailsSubmitted],
    };
    if (!forms[mode]) return undefined;
    const [tracker, submitted] = forms[mode];
    const report = () => {
      if (!submitted.current) tracker.current.abandoned();
    };
    const onHide = () => {
      if (document.visibilityState === "hidden") report();
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", report);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", report);
      report();
    };
  }, [mode]);

  // The refused step and why: error_code separates "the form is confusing"
  // from "our work-email rule is turning people away".
  const trackSignupFailed = (step, err) => {
    const errorCode = err.response?.data?.detail?.code || "UNKNOWN";
    const blocked = BLOCKED_EMAIL_REASONS[errorCode];
    track(EVENTS.SIGNUP_FAILED, {
      step,
      error_code: errorCode,
      status: err.response?.status || 0,
      // A personal or temporary address turned away: why, and its domain
      // only, never the address
      ...(blocked && {
        reason: blocked,
        email_domain: email.trim().split("@")[1]?.toLowerCase() || undefined,
      }),
    });
  };

  // The emailed link (/signup?link=...). Checking it uses nothing up: on a
  // laptop it opens step 3 already verified, on a phone it says to use a
  // laptop, and only creating the account spends it.
  const openLink = async (token) => {
    setMode("signupLinkCheck");
    let data = { status: "invalid" };
    try {
      ({ data } = await axios.post(`${host}/api/auth/signup/link-status`, {
        token,
        device: handheld ? "phone" : "laptop",
      }));
    } catch {
      /* unreachable or refused: shown as a link that doesn't work */
    }
    track(EVENTS.SIGNUP_LINK_OPENED, {
      device: handheld ? "phone" : "laptop",
      status: data.status,
      // Opened on a laptop while it still works: step 3 opens, the link is done
      consumed: Boolean(data.consumed),
      link_age_hours: data.linkAgeHours ?? undefined,
    });
    if (data.status === "expired") {
      track(EVENTS.SIGNUP_LINK_EXPIRED, {
        device: handheld ? "phone" : "laptop",
        link_age_hours: data.linkAgeHours ?? undefined,
      });
    }
    // The token stays out of the address bar, history and anything copied from it
    const params = new URLSearchParams(window.location.search);
    params.delete("link");
    window.history.replaceState(null, "", `/signup${params.toString() ? `?${params}` : ""}`);

    setEmail(data.email || "");
    if (data.status === "valid" && !handheld) {
      clearSignupStore();
      sessionStorage.setItem(STORE.stage, "details");
      sessionStorage.setItem(STORE.email, data.email);
      sessionStorage.setItem(STORE.token, token);
      setSignupToken(token);
      setMode("signupDetails");
    } else if (data.status === "valid") {
      setLinkExpiresAt(data.expiresAt || "");
      setMode("signupLinkOnPhone");
    } else if (data.status === "expired") {
      setMode("signupLinkExpired");
    } else if (data.status === "used") {
      if (signedInWithRole()) navigate("/role-redirect", { replace: true });
      else setMode("signupLinkUsed");
    } else {
      setMode("signupLinkInvalid");
    }
  };

  // Opening /signup again: carry on where this tab left off.
  useEffect(() => {
    if (initialMode !== "signupStart") return;
    const link = new URLSearchParams(window.location.search).get("link");
    if (link) {
      // Once per page load, so the opening is checked and counted once
      if (!linkOpened.current) {
        linkOpened.current = true;
        openLink(link);
      }
      return;
    }
    const token = sessionStorage.getItem("accessToken");
    if (token) {
      let valid = false;
      try {
        const { exp } = jwtDecode(token);
        valid = Boolean(exp && exp * 1000 > Date.now());
      } catch {
        /* unreadable: treated as expired */
      }
      if (valid) {
        // Someone already set up (has a role) belongs in the app. Without a
        // role it is an old-flow account that verified but never finished.
        if (hasRole(token)) {
          navigate("/role-redirect", { replace: true });
        } else {
          setEmail(sessionStorage.getItem(STORE.email) || "");
          setMode("signupDetails");
        }
        return;
      }
      sessionStorage.removeItem("accessToken");
    }
    const stage = sessionStorage.getItem(STORE.stage);
    const savedEmail = sessionStorage.getItem(STORE.email) || "";
    const savedToken = sessionStorage.getItem(STORE.token) || "";
    if (stage === "details" && savedToken && savedEmail) {
      setEmail(savedEmail);
      setSignupToken(savedToken);
      setMode("signupDetails");
    } else if (stage === "verify" && savedEmail) {
      setEmail(savedEmail);
      setCodeSentTo(savedEmail);
      setSentFrom(sessionStorage.getItem(STORE.sentFrom) || "");
      setMode("signupVerify");
    } else if (stage === "linkSent" && savedEmail) {
      setEmail(savedEmail);
      setLinkExpiresAt(sessionStorage.getItem(STORE.linkExpiresAt) || "");
      setMode("signupLinkSent");
    } else {
      clearSignupStore();
    }
    // openLink runs once, for the address the page opened on
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMode, navigate]);

  // Cooldown on "resend code" -- each one is another email.
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const storeSessionAndRedirect = (tokens) => {
    sessionStorage.setItem("accessToken", tokens.access_token);
    if (tokens.refresh_token) {
      localStorage.setItem("refreshToken", tokens.refresh_token);
    }
    navigate("/role-redirect");
  };

  // The onboarding endpoints answer with {detail: {code, message}}; the older
  // auth endpoints answer with a plain string. Rendering the object directly
  // would crash React, so always come away with a string.
  const apiError = (err, fallback) => {
    const detail = err.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      return (detail[0]?.msg || "").replace("Value error, ", "") || fallback;
    }
    return detail?.message || fallback;
  };

  // Cognito checks the full policy and says which rule failed; this only
  // catches the obvious case before the round trip.
  const passwordOk = password.length >= 8;
  const domain = (email.split("@")[1] || "").toLowerCase();

  const codeSent = (data, fallbackEmail) => {
    const sentTo = data.email || fallbackEmail;
    setEmail(sentTo);
    setCodeSentTo(data.codeSentTo || sentTo);
    setSentFrom(data.sentFrom || "");
    setResendIn(RESEND_SECONDS);
    sessionStorage.setItem(STORE.stage, "verify");
    sessionStorage.setItem(STORE.email, sentTo);
    sessionStorage.setItem(STORE.sentFrom, data.sentFrom || "");
  };

  const linkSent = (data, fallbackEmail) => {
    const sentTo = data.email || fallbackEmail;
    setEmail(sentTo);
    setLinkExpiresAt(data.linkExpiresAt || "");
    clearSignupStore();
    sessionStorage.setItem(STORE.stage, "linkSent");
    sessionStorage.setItem(STORE.email, sentTo);
    sessionStorage.setItem(STORE.linkExpiresAt, data.linkExpiresAt || "");
    setMode("signupLinkSent");
  };

  // An email that has an account, or a company that has one, gets its own
  // screen instead of an error line. -> true when it did.
  const showTurnedAway = (err) => {
    const detail = err.response?.data?.detail || {};
    if (detail.code === "ACCOUNT_EXISTS" && mode === "signupStart" && !handheld) {
      // On a laptop step 1 says it under the field, like the personal-email error
      setError(
        <>
          An account already exists with this email.{" "}
          <button type="button" className="gh-link gh-error-link" onClick={() => showSignIn("login")}>
            Sign in
          </button>
        </>
      );
      return true;
    }
    if (detail.code === "ACCOUNT_EXISTS") {
      setError("");
      setMode("signupAccountExists");
      return true;
    }
    if (detail.code === "DOMAIN_ALREADY_REGISTERED") {
      setError("");
      setJoin({
        status: detail.joinRequestedAt ? "waiting" : "ask",
        domain: detail.domain,
        requestedAt: detail.joinRequestedAt,
      });
      setMode("signupJoin");
      return true;
    }
    return false;
  };

  const handleSignupStart = async (e) => {
    e.preventDefault();
    setError(""); setInfo("");
    setSubmitting(true);
    credentialsSubmitted.current = true;
    track(EVENTS.SIGNUP_SUBMITTED, { device: handheld ? "phone" : "desktop" });
    // Our own team and QA, flagged from here on (no account yet)
    setInternal(email.trim());
    try {
      const { data } = await axios.post(`${host}/api/auth/signup/start`, {
        workEmail: email.trim(),
        device: handheld ? "phone" : "desktop",
        attribution: getAttribution() || undefined,
        // Phones get a link instead of a code; the lifecycle router's
        // follow-ups to it depend on the country (looked up on page load)
        ...(handheld && {
          country: (await getCountryHint()) || undefined,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || undefined,
        }),
      });
      // Meta: step 1 accepted, by code or by link. Only the email is known
      // here: no phone, no account, and no country yet.
      trackSignupConversion("Lead", email.trim(), {
        eventId: data.signupId ? `lead_${data.signupId}` : undefined,
      });
      if (data.nextStep === "open_link") {
        track(EVENTS.SIGNUP_LINK_SENT, { attempt: data.linkSends || 1 });
        linkSent(data, email.trim());
        return;
      }
      track(EVENTS.SIGNUP_CODE_SENT);
      codeSent(data, email.trim());
      setCodeDigits(Array(CODE_LENGTH).fill(""));
      setMode("signupVerify");
    } catch (err) {
      trackSignupFailed("start", err);
      if (!showTurnedAway(err)) {
        setError(apiError(err, "We couldn't start your signup. Please try again."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // "Resend the link", "Email me the link again" (the same link) and, once it
  // has expired, "Email me a new link"
  const handleResendLink = async () => {
    setError(""); setInfo("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${host}/api/auth/signup/resend-link`, { workEmail: email });
      track(EVENTS.SIGNUP_LINK_RESENT, { after_expiry: mode === "signupLinkExpired" });
      linkSent(data, email);
      setInfo(mode === "signupLinkExpired" ? "We've sent you a new link." : "We've sent the link again.");
    } catch (err) {
      trackSignupFailed("resend_link", err);
      if (!showTurnedAway(err)) {
        setError(apiError(err, "We couldn't send the link. Please try again shortly."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // "Ask to join" and "Send a reminder": emails the company's admins
  const handleJoinRequest = async () => {
    setError(""); setInfo("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${host}/api/auth/signup/join-request`, { workEmail: email });
      if (data.status !== "waiting") {
        track(EVENTS.SIGNUP_JOIN_REQUESTED, { reminder: data.status === "reminded" });
      } else if (join?.status === "waiting") {
        setInfo(`You can send the next reminder after ${formatUntil(data.nextReminderAt)}.`);
      }
      setJoin(data);
    } catch (err) {
      trackSignupFailed("join_request", err);
      if (!showTurnedAway(err)) {
        setError(apiError(err, "We couldn't reach your team. Please try again shortly."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignupVerify = async (e) => {
    e.preventDefault();
    setError(""); setInfo("");
    setSubmitting(true);
    track(EVENTS.SIGNUP_CODE_SUBMITTED);
    try {
      const { data } = await axios.post(`${host}/api/auth/signup/verify`, {
        workEmail: email,
        code: signupCode,
      });
      // signed_in: an account from the old flow, finishing with a session;
      // a new signup has no account until step 3
      track(EVENTS.SIGNUP_VERIFIED, { signed_in: Boolean(sessionStorage.getItem("accessToken")) });
      setSignupToken(data.signupToken);
      sessionStorage.setItem(STORE.stage, "details");
      sessionStorage.setItem(STORE.token, data.signupToken);
      setMode("signupDetails");
    } catch (err) {
      trackSignupFailed("verify", err);
      setError(apiError(err, "We couldn't verify that code. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const focusCodeBox = (i) => codeRefs.current[i]?.focus();

  const handleCodeInput = (i, raw) => {
    let digits = raw.replace(/\D/g, "");
    if (!digits) {
      setCodeDigits((prev) => prev.map((d, k) => (k === i ? "" : d)));
      return;
    }
    // Typing into a box that already holds a digit appends instead of replacing
    // unless the browser selected it first, so drop the character we started with.
    if (digits.length > 1 && digits[0] === codeDigits[i]) digits = digits.slice(1);

    setCodeDigits((prev) => {
      const next = [...prev];
      digits.split("").forEach((d, k) => {
        if (i + k < CODE_LENGTH) next[i + k] = d;
      });
      return next;
    });
    focusCodeBox(Math.min(i + digits.length, CODE_LENGTH - 1));
  };

  const handleCodeKeyDown = (i, e) => {
    if (e.key === "Backspace" && !codeDigits[i] && i > 0) {
      // Backspace in an empty box clears the one before it and steps back,
      // which is what people expect from a code field.
      e.preventDefault();
      setCodeDigits((prev) => prev.map((d, k) => (k === i - 1 ? "" : d)));
      focusCodeBox(i - 1);
    } else if (e.key === "ArrowLeft" && i > 0) {
      e.preventDefault();
      focusCodeBox(i - 1);
    } else if (e.key === "ArrowRight" && i < CODE_LENGTH - 1) {
      e.preventDefault();
      focusCodeBox(i + 1);
    }
  };

  const handleCodePaste = (e) => {
    const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, CODE_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    setCodeDigits(Array.from({ length: CODE_LENGTH }, (_, k) => pasted[k] || ""));
    focusCodeBox(Math.min(pasted.length, CODE_LENGTH) - 1);
  };

  const handleResendCode = async () => {
    setError(""); setInfo("");
    try {
      const { data } = await axios.post(`${host}/api/auth/signup/resend-code`, {
        workEmail: email,
      });
      track(EVENTS.SIGNUP_CODE_RESENT);
      codeSent(data, email);
      setCodeDigits(Array(CODE_LENGTH).fill(""));
      setInfo("We've sent you a new code.");
    } catch (err) {
      trackSignupFailed("resend_code", err);
      setError(apiError(err, "We couldn't send another code. Please try again shortly."));
    }
  };

  // Back to step 1 with the address still filled in
  const changeEmail = () => {
    clearSignupStore();
    credentialsSubmitted.current = false;
    setError(""); setInfo("");
    setCodeDigits(Array(CODE_LENGTH).fill(""));
    setMode("signupStart");
  };

  // -> true when the session now carries a role. Never throws: the caller
  // has already created the organization and must not undo that over a token.
  const refreshSessionForNewGroup = async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) return false;
    try {
      const { data } = await axios.post(`${host}/api/auth/refresh`, {
        refresh_token: refreshToken,
      });
      if (!data.access_token) return false;

      sessionStorage.setItem("accessToken", data.access_token);

      // Confirm rather than assume. If the claim still isn't there, sending
      // them to role-redirect would sign them straight back out.
      const groups = jwtDecode(data.access_token)["cognito:groups"] || [];
      return groups.length > 0;
    } catch {
      return false;
    }
  };

  const handleSignupComplete = async (e) => {
    e.preventDefault();
    setError(""); setInfo("");
    if (!details.phoneNumber || !isValidPhoneNumber(details.phoneNumber)) {
      setError("Please enter a valid mobile number with country code.");
      return;
    }
    if (!finishingOldAccount && !passwordOk) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    detailsSubmitted.current = true;
    track(EVENTS.SIGNUP_DETAILS_SUBMITTED);
    // No country field: the country is the one the phone number belongs to.
    const country = countryFromPhone(details.phoneNumber);
    const body = {
      name: details.name,
      phoneNumber: details.phoneNumber,
      country,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      // Agreed on step 1 ("By continuing you agree to..."), or on this step
      // for an old-flow account
      acceptedPrivacyPolicy: true,
      attribution: getAttribution() || undefined,
    };
    try {
      let signedIn;
      let userId;
      let orgId;
      if (finishingOldAccount) {
        ({ data: { orgId } = {} } = await axios.post(`${host}/api/auth/signup/complete`, body, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("accessToken")}` },
        }));
        // The token in hand predates the account joining Company_Admin, so its
        // cognito:groups claim is empty and role-redirect would bounce them
        // back here. Cognito re-reads group membership on refresh.
        signedIn = await refreshSessionForNewGroup();
      } else {
        const { data } = await axios.post(`${host}/api/auth/signup/create-account`, {
          ...body,
          signupToken,
          password,
        });
        // Issued after the account joined Company_Admin, so it already has the role
        signedIn = Boolean(!data.requiresLogin && data.access_token);
        userId = data.userId;
        orgId = data.orgId;
        if (signedIn) {
          sessionStorage.setItem("accessToken", data.access_token);
          if (data.refresh_token) localStorage.setItem("refreshToken", data.refresh_token);
        }
      }
      if (!userId) {
        try {
          userId = jwtDecode(sessionStorage.getItem("accessToken")).sub;
        } catch {
          /* no session: the event goes without an ID */
        }
      }
      // signed_up itself is sent by the server; this links the anonymous
      // signup-page visit to the new account so the funnel joins up, and puts
      // signup_completed under the new company with is_internal like every
      // event after login.
      identifyUser(userId, {
        email,
        name: details.name,
        phone: details.phoneNumber,
        // Step 1's line covers updates on WhatsApp
        waOptIn: true,
      });
      setCompany(orgId, { isInternal: isInternalEmail(email) });
      track(EVENTS.SIGNUP_COMPLETED, { country });
      // Meta: the signup marketing pays for. Waits for the event to leave,
      // since leaving /signup reloads the page (see adTracking).
      await trackSignupConversion("CompleteRegistration", email, {
        eventId: userId ? `signup_${userId}` : undefined,
        match: {
          phone: details.phoneNumber,
          userId,
          country: details.phoneNumber ? parsePhoneNumber(details.phoneNumber)?.country : undefined,
        },
        // Teammates join through Team, never this page
        data: { signup_method: "work_email", signup_type: "new_account" },
      });

      clearSignupStore();
      setSignupToken("");

      if (!signedIn) {
        // The account exists either way -- say so and let them in through
        // the front door rather than dropping them at a blank login.
        setPassword("");
        setMode("login");
        setInfo("Your account is ready. Please sign in to continue.");
        return;
      }

      navigate("/role-redirect");
    } catch (err) {
      trackSignupFailed("details", err);
      const code = err.response?.data?.detail?.code;
      if (code === "SIGNUP_ALREADY_USED") {
        clearSignupStore();
        setSignupToken("");
        setMode("signupLinkUsed");
        return;
      }
      if (["SIGNUP_EXPIRED", "ACCOUNT_EXISTS", "DOMAIN_ALREADY_REGISTERED"].includes(code)) {
        clearSignupStore();
        setSignupToken("");
        credentialsSubmitted.current = false;
      }
      if (showTurnedAway(err)) return;
      if (code === "SIGNUP_EXPIRED") {
        // The verification ran out: the account does not exist, start over
        setMode("signupStart");
      }
      setError(apiError(err, "We couldn't finish setting up your account. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  // Login and signup are one page on two addresses, / and /signup. Switching
  // changes the address, so a signup started from the login form is on
  // /signup like one from an ad and counts as a signup page view. Each route
  // has its own key (App.jsx), so the page starts fresh on the new address and
  // Back shows the right form. The query string (campaign tags) goes along.
  const goToSignup = () => {
    // Already on /signup (shown the login form after signing up): navigating
    // to the same address would do nothing, so switch the form here
    if (window.location.pathname === "/signup") {
      signupViewTracked.current = false;
      credentialsSubmitted.current = false;
      setMode("signupStart");
      setError(""); setInfo("");
      setPassword("");
      return;
    }
    navigate({ pathname: "/signup", search: window.location.search });
  };
  const goToLogin = () => navigate({ pathname: "/", search: window.location.search });

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const response = await axios.post(`${host}/api/auth/login`, {
        email: email.trim(),
        password,
      });

      if (response.data.challenge_name === "NEW_PASSWORD_REQUIRED") {
        setSession(response.data.session);
        setMode("newPassword");
      } else if (!hasRole(response.data.access_token)) {
        // An old-flow account: verified its email but never finished, so there
        // is no organization and no role yet. role-redirect would sign them out
        // and /signup would refuse the email as taken; finish the wizard here.
        sessionStorage.setItem("accessToken", response.data.access_token);
        if (response.data.refresh_token) {
          localStorage.setItem("refreshToken", response.data.refresh_token);
        }
        clearSignupStore();
        sessionStorage.setItem(STORE.stage, "details");
        sessionStorage.setItem(STORE.email, email.trim());
        setSignupToken("");
        setInfo("");
        setMode("signupDetails");
      } else {
        storeSessionAndRedirect(response.data);
      }
    } catch (err) {
      setError(getErrorMessage(err, "Login failed. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteNewPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmNewPassword) {
      setError("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${host}/api/auth/complete-new-password`, {
        email,
        new_password: newPassword,
        session,
      });
      storeSessionAndRedirect(response.data);
    } catch (err) {
      setError(getErrorMessage(err, "Could not set new password. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPasswordRequest = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setSubmitting(true);
    try {
      const response = await axios.post(`${host}/api/auth/forgot-password`, { email });
      setInfo(response.data.message || "If that email is registered, a reset code has been sent.");
      setMode("forgotConfirm");
    } catch (err) {
      setError(getErrorMessage(err, "Could not start password reset. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmNewPassword) {
      setError("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${host}/api/auth/confirm-forgot-password`, {
        email,
        code: resetCode,
        new_password: newPassword,
      });
      setInfo("Password reset successfully. Please log in.");
      setMode("login");
      setPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
      setResetCode("");
    } catch (err) {
      setError(getErrorMessage(err, "Could not reset password. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const showSignIn = (target = "login") => {
    setMode(target);
    setError("");
    setInfo("");
  };

  const messages = (
    <>
      {error && <p className="gh-error" role="alert">{error}</p>}
      {info && <p className="gh-info">{info}</p>}
    </>
  );

  const passwordField = (id, label, value, onChange, autoComplete, extra = {}) => (
    <div className="gh-field">
      <label htmlFor={id} className="gh-label">{label}</label>
      <div className="gh-password">
        <input
          id={id}
          type={showPassword ? "text" : "password"}
          autoComplete={autoComplete}
          className="gh-input"
          value={value}
          onChange={onChange}
          required
          {...extra}
        />
        <button type="button" className="gh-show" onClick={() => setShowPassword((s) => !s)}>
          {showPassword ? "Hide" : "Show"}
        </button>
      </div>
    </div>
  );

  const progress = (label, done) => (
    <div className="gh-progress">
      <span className="gh-eyebrow">{label}</span>
      <div className="gh-progress-bars" aria-hidden="true">
        {[1, 2, 3].map((n) => <span key={n} className={n <= done ? "on" : ""} />)}
      </div>
    </div>
  );

  const allowanceBars = (title) => (
    <div className="gh-allowance">
      <div className="gh-allowance-head">
        <span className="gh-eyebrow">{title}</span>
        <span className="gh-mono">{domain}</span>
      </div>
      <div className="gh-allowance-rows">
        <div className="gh-allowance-row">
          <span>CV screenings</span>
          <div className="gh-bar" aria-hidden="true" />
          <span>{FREE_CV_SCREENINGS}</span>
        </div>
        <div className="gh-allowance-row">
          <span>AI interviews</span>
          <div className="gh-bar" aria-hidden="true" />
          <span>{FREE_INTERVIEWS}</span>
        </div>
      </div>
    </div>
  );

  const changeEmailLink = (label = "Wrong email? Change it") => (
    <button type="button" className="gh-link" onClick={changeEmail}>{label}</button>
  );
  const allowance = `${FREE_CV_SCREENINGS} screenings + ${FREE_INTERVIEWS} interviews`;

  // ---------------- phone and tablet: the emailed link ----------------
  if (mode === "signupLinkCheck") {
    return (
      <StatusScreen eyebrow="One moment" title="Opening your link...">
        <p className="gh-status-text">Checking the link from your email.</p>
      </StatusScreen>
    );
  }

  if (mode === "signupLinkSent") {
    return (
      <StatusScreen
        eyebrow="Link sent"
        title={handheld ? "Open the link" : "Check your inbox."}
        accent={handheld ? "on your laptop." : "Your link is there."}
      >
        <p className="gh-status-text">
          We sent it to <strong>{email}</strong>.{" "}
          {handheld
            ? "Your account is set up there, where you'll upload CVs and watch interview answers."
            : "Click it to set up your account."}
        </p>
        <div className="gh-panel">
          <LaptopPicture />
          <ol className="gh-panel-steps">
            <li><span>1</span><span>Open your email on your laptop or desktop.</span></li>
            <li><span>2</span><span>Find the email from GenieHire: <strong>{LINK_EMAIL_SUBJECT}</strong>.</span></li>
            <li><span>3</span><span>Click <strong>Set up my account</strong>. Your email is verified in the same click.</span></li>
          </ol>
        </div>
        {messages}
        <div className="gh-actions">
          <button type="button" className="gh-link" onClick={handleResendLink} disabled={submitting}>
            {submitting ? "Sending..." : "Resend the link"}
          </button>
          {changeEmailLink()}
        </div>
        <p className="gh-hand" style={{ fontSize: 20, transform: "rotate(-2deg)" }}>{allowance}, set aside for you.</p>
      </StatusScreen>
    );
  }

  if (mode === "signupLinkOnPhone") {
    return (
      <StatusScreen eyebrow="You're on a phone" title="This link opens" accent="on a laptop.">
        <p className="gh-status-text">
          GenieHire works on a laptop or desktop, where you upload CVs and play candidates' interview answers.
          Your link still works: open the same email on your laptop and click it there.
        </p>
        <div className="gh-panel gh-facts">
          <div><span>Email</span><strong>{email}</strong></div>
          <hr />
          {linkExpiresAt && (
            <>
              <div><span>Link works until</span><strong>{formatUntil(linkExpiresAt)}</strong></div>
              <hr />
            </>
          )}
          <div><span>Set aside for you</span><strong>{allowance}</strong></div>
        </div>
        {messages}
        <div className="gh-actions gh-actions-center">
          <button type="button" className="gh-button gh-button-outline" onClick={handleResendLink} disabled={submitting}>
            {submitting ? "Sending..." : "Email me the link again"}
          </button>
          {changeEmailLink()}
        </div>
        <p className="gh-hand" style={{ fontSize: 20, transform: "rotate(-2deg)" }}>See you on the big screen.</p>
      </StatusScreen>
    );
  }

  if (mode === "signupLinkExpired") {
    return (
      <StatusScreen eyebrow="Link expired" title="This link has expired.">
        <p className="gh-status-text">
          Links last 7 days. Your {FREE_CV_SCREENINGS} screenings and {FREE_INTERVIEWS} interviews are still set
          aside for <strong>{email.split("@")[1] || "your company"}</strong>.
        </p>
        {messages}
        <button type="button" className="gh-button" onClick={handleResendLink} disabled={submitting || !email}>
          {submitting ? "Sending..." : "Email me a new link"}
        </button>
        <NoteBox>
          The new link goes to <strong>{email}</strong>.{" "}
          {handheld ? "Open it on your laptop." : "Opened on this laptop, it works straight away."}
        </NoteBox>
      </StatusScreen>
    );
  }

  if (mode === "signupLinkUsed") {
    return (
      <StatusScreen eyebrow="Link already used" title="This link has already been used.">
        <p className="gh-status-text">
          {email ? <>Your account for <strong>{email}</strong> is set up. </> : "Your account is set up. "}
          {handheld ? <>Sign in on your laptop at <strong>{PLATFORM_ADDRESS}</strong>.</> : "Sign in to carry on."}
        </p>
        {!handheld && <button type="button" className="gh-button" onClick={goToLogin}>Sign in</button>}
      </StatusScreen>
    );
  }

  if (mode === "signupLinkInvalid") {
    return (
      <StatusScreen eyebrow="Link not working" title="This link doesn't work.">
        <p className="gh-status-text">
          It may have been cut short when it was copied. Start again with your work email and we'll send a new one.
        </p>
        <button type="button" className="gh-button" onClick={changeEmail}>Start again</button>
      </StatusScreen>
    );
  }

  // ---------------- turned away: existing person or company ----------------
  if (mode === "signupAccountExists") {
    return (
      <StatusScreen eyebrow="You already have an account" title="Welcome back.">
        <p className="gh-status-text">
          <strong>{email}</strong> already has an account.{" "}
          {handheld && <>Sign in on your laptop at <strong>{PLATFORM_ADDRESS}</strong>.</>}
        </p>
        {!handheld && (
          <div className="gh-actions gh-actions-center">
            <button type="button" className="gh-button" onClick={goToLogin}>Sign in</button>
            <button type="button" className="gh-link" onClick={() => showSignIn("forgotRequest")}>
              Forgot your password?
            </button>
          </div>
        )}
        <div className="gh-actions">{changeEmailLink("Use a different work email")}</div>
      </StatusScreen>
    );
  }

  if (mode === "signupJoin" && join) {
    const domainName = join.domain || email.split("@")[1];
    let screen;
    if (join.status === "sent" || join.status === "reminded") {
      screen = {
        eyebrow: join.status === "sent" ? "Join request sent" : "Reminder sent",
        title: join.status === "sent" ? "Request sent to your team." : "Reminder sent to your team.",
        body: <>We'll email <strong>{email}</strong> as soon as an admin adds you.</>,
      };
    } else if (join.status === "waiting") {
      screen = {
        eyebrow: "Request still waiting",
        title: "Your request is with your team.",
        body: <>You asked to join {daysAgo(join.requestedAt)} and no admin has added you yet. We'll email{" "}
          <strong>{email}</strong> as soon as one does.</>,
        action: (
          <>
            <button type="button" className="gh-button gh-button-outline" onClick={handleJoinRequest} disabled={submitting}>
              {submitting ? "Sending..." : "Send a reminder"}
            </button>
            <p className="gh-small" style={{ textAlign: "center" }}>One reminder per day.</p>
          </>
        ),
      };
    } else {
      screen = {
        eyebrow: "Company already on GenieHire",
        title: "Join your team.",
        body: <>Your team at <strong>{domainName}</strong> already uses GenieHire. Join them and share the same allowance.</>,
        action: (
          <>
            <button type="button" className="gh-button" onClick={handleJoinRequest} disabled={submitting}>
              {submitting ? "Sending..." : "Ask to join"}
            </button>
            <p className="gh-small" style={{ textAlign: "center" }}>We email the account's admins. We never show who they are.</p>
          </>
        ),
      };
    }
    return (
      <StatusScreen eyebrow={screen.eyebrow} title={screen.title}>
        <p className="gh-status-text">{screen.body}</p>
        {messages}
        {screen.action}
        <div className="gh-actions gh-actions-center">{changeEmailLink("Use a different work email")}</div>
      </StatusScreen>
    );
  }

  // ---------------- step 1 on a phone or tablet ----------------
  if (mode === "signupStart" && handheld) {
    return (
      <PhoneLanding
        note={note}
        email={email}
        setEmail={setEmail}
        emailRef={emailRef}
        emailFieldProps={credentialFields.current.bind("workEmail")}
        onSubmit={handleSignupStart}
        submitting={submitting}
        messages={messages}
        goToLogin={goToLogin}
      />
    );
  }

  // ---------------- step 1: work email ----------------
  if (mode === "signupStart") {
    const scrollToForm = () => {
      document.getElementById("signup")?.scrollIntoView({ behavior: "smooth", block: "center" });
      emailRef.current?.focus({ preventScroll: true });
    };
    return (
      <div className="gh-page">
        <header className="gh-header">
          <Logo />
          <p className="gh-header-note">
            Already have an account?{" "}
            <button type="button" className="gh-link" style={{ fontWeight: 600 }} onClick={goToLogin}>
              Sign in
            </button>
          </p>
        </header>

        <main>
          <section className="gh-hero">
            <div className="gh-col" style={{ gap: 18 }}>
              <p className="gh-hand" style={{ fontSize: 21, transform: "rotate(-2deg)" }}>{note}</p>
              <h1 className="gh-h1">
                <span>Your first hire</span>
                <span className="gh-accent">is on us.</span>
              </h1>
              <p className="gh-lead">
                We run the first-round video interviews for your next role. You watch the answers and
                pick who you hire.
              </p>

              <div className="gh-example">
                <div className="gh-example-head">
                  <span className="gh-mono">Wed 9:02 am · Inside sales · 7 of 10 done</span>
                  <span className="gh-example-tag">Example</span>
                </div>
                {EXAMPLE_ROWS.map((row) => (
                  <div key={row.name} className="gh-example-row">
                    <span className="gh-play" aria-hidden="true">
                      <svg width="12" height="12" viewBox="0 0 12 12"><path d="M3 1.5 L10.5 6 L3 10.5 Z" fill="#FFFFFF" /></svg>
                    </span>
                    <span className="gh-example-name">{row.name}</span>
                    <span className="gh-mono gh-example-when">Completed {row.when}</span>
                    <span className="gh-example-score">{row.score}</span>
                  </div>
                ))}
              </div>
              <p className="gh-hand" style={{ fontSize: 17, marginTop: -5, transform: "rotate(-1.5deg)" }}>
                Interviewed while you were asleep.
              </p>
            </div>

            <div className="gh-col">
              <form id="signup" className="gh-ticket" onSubmit={handleSignupStart}>
                <div className="gh-ticket-top">
                  <span className="gh-ticket-num">{FREE_INTERVIEWS}</span>
                  <span className="gh-ticket-title">
                    <strong>AI video interviews, free</strong>
                    <span>Plus {FREE_CV_SCREENINGS} CV screenings if you need them</span>
                  </span>
                </div>
                <div className="gh-perforation" aria-hidden="true"><span /><span /></div>
                <div className="gh-ticket-body">
                  <p className="gh-ticket-intro">Claim them with your work email</p>
                  <label htmlFor="gh-email" className="gh-label">Work email</label>
                  <input
                    id="gh-email"
                    ref={emailRef}
                    type="email"
                    autoComplete="email"
                    placeholder="you@yourcompany.com"
                    className="gh-input gh-input-lg"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    {...credentialFields.current.bind("workEmail")}
                    required
                  />
                  {messages}
                  <button type="submit" className="gh-button" disabled={submitting}>
                    {submitting ? "Sending your code..." : <>Claim my {FREE_INTERVIEWS} interviews<Arrow /></>}
                  </button>
                  <p className="gh-fineprint">No card required. No sales call. <Agreement /></p>
                  <div className="gh-rule" />
                  <p className="gh-aside">
                    Hiring 20 or more people a month?{" "}
                    <a
                      href={`mailto:${SALES_EMAIL}`}
                      style={{ fontWeight: 600 }}
                      onClick={() => track(EVENTS.SIGNUP_TALK_TO_SALES_CLICKED, {}, { transport: "sendBeacon" })}
                    >
                      Talk to our team
                    </a>
                  </p>
                </div>
              </form>
            </div>
          </section>

          <section className="gh-band">
            <div className="gh-section gh-how">
              <div className="gh-how-head">
                <span className="gh-eyebrow">How the AI round works</span>
                <h2 className="gh-h2">AI interviews. <span className="gh-accent">You decide.</span></h2>
              </div>
              <ol className="gh-steps">
                <li>
                  <span>1</span>
                  <h3>Send your shortlist one link</h3>
                  <p>No call slots to fix, no "not reachable", no "call me later".</p>
                </li>
                <li>
                  <span>2</span>
                  <h3>They interview in their own time</h3>
                  <p>On a laptop, evenings included. Every candidate gets the same questions for your role.</p>
                </li>
                <li>
                  <span>3</span>
                  <h3>You get scores, recordings and transcripts</h3>
                  <p>Play any answer and decide who you meet in person.</p>
                </li>
              </ol>
            </div>
          </section>

          <section className="gh-section gh-faq">
            <div>
              <h3>Already shortlisted in your own tools?</h3>
              <p>Upload just those candidates' CVs and send them the interview link. The free CV screenings cover it.</p>
            </div>
            <div>
              <h3>Do candidates need to install anything?</h3>
              <p>No. They open the link on a laptop and start.</p>
            </div>
            <div>
              <h3>Why a work email?</h3>
              <p>
                The free interviews belong to your company. Teammates you invite share them. When they run
                out, you choose a plan in the app; nothing is charged on its own.
              </p>
            </div>
          </section>

          <section className="gh-section gh-cta-wrap">
            <div className="gh-cta">
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <p className="gh-cta-title">
                  Your first hire is on us.
                </p>
                <p className="gh-cta-sub">
                  {FREE_INTERVIEWS} AI video interviews free for your company. No card required.
                </p>
              </div>
              <button type="button" className="gh-button gh-button-light" onClick={scrollToForm}>
                Claim my {FREE_INTERVIEWS} interviews<Arrow color="#141414" />
              </button>
            </div>
          </section>
        </main>

        <footer className="gh-footer">
          <span>© {new Date().getFullYear()} NSquare Innovators Inc. · GenieHire.ai</span>
          <nav>
            <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Privacy</a>
            <a href={TERMS_URL} target="_blank" rel="noreferrer">Terms</a>
            <a href="/grievance-form">Help</a>
          </nav>
        </footer>
      </div>
    );
  }

  // ---------------- step 2: the emailed code ----------------
  if (mode === "signupVerify") {
    return (
      <div className="gh-page">
        <header className="gh-header">
          <Logo />
          {progress("Step 2 of 3", 2)}
        </header>
        <main className="gh-split">
          <div className="gh-col" style={{ gap: 22 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              <h1 className="gh-h1-step">Check your inbox. <span className="gh-accent">One code to go.</span></h1>
              <p className="gh-lead-step">
                We sent a 6-digit code to{" "}
                <strong style={{ fontWeight: 600, overflowWrap: "anywhere" }}>{codeSentTo || email}</strong>.
                It keeps the free allowance with your company.
              </p>
            </div>
            {allowanceBars("Being set up for")}
            <p className="gh-hand" style={{ fontSize: 18, transform: "rotate(-2deg)", maxWidth: "20em" }}>
              Your free round is almost ready.
            </p>
          </div>

          <form className="gh-card" style={{ gap: 18 }} onSubmit={handleSignupVerify}>
            <h2 className="gh-card-title">Enter the code</h2>
            <fieldset style={{ margin: 0, padding: 0, border: 0, display: "flex", flexDirection: "column", gap: 8 }}>
              <legend className="gh-label" style={{ padding: 0, marginBottom: 8 }}>6-digit code from your email</legend>
              <div className="gh-code" onPaste={handleCodePaste}>
                {codeDigits.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (codeRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    autoComplete={i === 0 ? "one-time-code" : "off"}
                    maxLength={1}
                    aria-label={`Digit ${i + 1} of ${CODE_LENGTH}`}
                    value={digit}
                    onChange={(e) => handleCodeInput(i, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(i, e)}
                    onFocus={(e) => e.target.select()}
                  />
                ))}
              </div>
            </fieldset>
            {messages}
            <button type="submit" className="gh-button" disabled={submitting || signupCode.length < CODE_LENGTH}>
              {submitting ? "Verifying..." : <>Verify and continue<Arrow /></>}
            </button>
            <div className="gh-help">
              <p>
                Not in your inbox? It comes from{" "}
                <strong style={{ fontWeight: 600, overflowWrap: "anywhere" }}>{sentFrom || "GenieHire"}</strong>.
                Check spam, Promotions or your company's quarantine folder.
              </p>
              <div className="gh-help-row">
                {resendIn > 0 ? (
                  <span>Resend code in 0:{String(resendIn).padStart(2, "0")}</span>
                ) : (
                  <button type="button" className="gh-link" onClick={handleResendCode}>Resend code</button>
                )}
                <button type="button" className="gh-link" onClick={changeEmail}>Wrong email? Change it</button>
              </div>
            </div>
          </form>
        </main>
      </div>
    );
  }

  // ---------------- step 3: name, mobile, password -> account ----------------
  if (mode === "signupDetails") {
    return (
      <div className="gh-page">
        <header className="gh-header">
          <Logo />
          {progress("Last step", 3)}
        </header>
        <main className="gh-split">
          <div className="gh-col" style={{ gap: 22 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              <h1 className="gh-h1-step">Your free round is <span className="gh-accent">set aside.</span></h1>
              <p className="gh-lead-step">One more step and you can paste your first JD.</p>
            </div>
            <div className="gh-allowance">
              <div className="gh-allowance-head">
                <span className="gh-eyebrow">Reserved for</span>
                <span className="gh-mono">{domain}</span>
              </div>
              <div className="gh-allowance-grid">
                <div>
                  <strong>{FREE_CV_SCREENINGS}</strong>
                  <span>CV screenings</span>
                </div>
                <div>
                  <strong>{FREE_INTERVIEWS}</strong>
                  <span>AI video interviews</span>
                </div>
              </div>
              <div className="gh-allowance-foot">
                The allowance belongs to {domain || "your company"}. Teammates you invite share it.
              </div>
            </div>
            <p className="gh-hand" style={{ fontSize: 18, transform: "rotate(-2deg)", maxWidth: "20em" }}>
              Next stop: describe your first hire.
            </p>
          </div>

          <form className="gh-card" onSubmit={handleSignupComplete}>
            <h2 className="gh-card-title">Finish your account</h2>

            {email && (
              <div className="gh-verified">
                <span><small>Work email</small><b>{email}</b></span>
                <span><Check />Verified</span>
              </div>
            )}

            <div className="gh-field">
              <label htmlFor="gh-name" className="gh-label">Your name</label>
              <input
                id="gh-name"
                type="text"
                autoComplete="name"
                placeholder="First and last name"
                className="gh-input"
                value={details.name}
                onChange={(e) => setDetails((prev) => ({ ...prev, name: e.target.value }))}
                {...detailFields.current.bind("name")}
                required
              />
            </div>

            <div className="gh-field">
              <label htmlFor="gh-mobile" className="gh-label">Mobile number</label>
              {/* Same setup as the company profile's contact number (EditCompanyProfile) */}
              <PhoneInput
                id="gh-mobile"
                international
                defaultCountry={phoneCountry}
                placeholder="+91 98765 43210"
                className="gh-input gh-phone"
                numberInputProps={{ autoComplete: "tel", required: true }}
                // On PhoneInput itself: it overrides onFocus/onBlur passed in
                // numberInputProps with its own, which call these
                {...detailFields.current.bind("phoneNumber")}
                value={details.phoneNumber}
                onChange={(value) => setDetails((prev) => ({ ...prev, phoneNumber: value || "" }))}
                countryCallingCodeEditable={true}
              />
              <span className="gh-hint">For account alerts, like when candidates finish their interviews.</span>
            </div>

            {!finishingOldAccount &&
              passwordField("gh-pass", "Create a password", password, (e) => setPassword(e.target.value),
                "new-password", { placeholder: "At least 8 characters", ...detailFields.current.bind("password") })}

            {messages}
            <button type="submit" className="gh-button" disabled={submitting}>
              {submitting ? "Setting up your account..." : <>Create account and start<Arrow /></>}
            </button>
            {finishingOldAccount ? (
              <p className="gh-fineprint"><Agreement /></p>
            ) : (
              <p className="gh-fineprint">No card required. No sales call.</p>
            )}
          </form>
        </main>
      </div>
    );
  }

  // ---------------- sign in, and its password screens ----------------
  const inputClass =
    "login-input w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#2960FD] text-[#333333] bg-white";
  const buttonClass =
    "w-full py-3 rounded-xl text-white font-semibold bg-gradient-to-r from-[#2960FD] to-[#FF60B6] disabled:opacity-60";

  let form;
  if (mode === "newPassword") {
    form = (
      <form onSubmit={handleCompleteNewPassword} className="space-y-4 px-4">
        <p className="text-sm text-[#667085]">This is your first login. Please set a new password.</p>
        <input
          type="password"
          placeholder="New password"
          className={inputClass}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="Confirm new password"
          className={inputClass}
          value={confirmNewPassword}
          onChange={(e) => setConfirmNewPassword(e.target.value)}
          required
        />
        {error && <p className="text-sm text-red-500">{error}</p>}
        <button type="submit" className={buttonClass} disabled={submitting}>
          {submitting ? "Setting password..." : "Set password & continue"}
        </button>
      </form>
    );
  } else if (mode === "forgotRequest") {
    form = (
      <form onSubmit={handleForgotPasswordRequest} className="space-y-4 px-4">
        <input
          type="text"
          placeholder="Email or Username"
          autoComplete="username"
          className={inputClass}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        {error && <p className="text-sm text-red-500">{error}</p>}
        {info && <p className="text-sm text-green-600">{info}</p>}
        <button type="submit" className={buttonClass} disabled={submitting}>
          {submitting ? "Sending..." : "Send reset code"}
        </button>
        <button
          type="button"
          className="w-full text-sm text-[#667085] hover:text-[#2960FD]"
          onClick={() => showSignIn()}
        >
          Back to login
        </button>
      </form>
    );
  } else if (mode === "forgotConfirm") {
    form = (
      <form onSubmit={handleConfirmForgotPassword} className="space-y-4 px-4">
        <input
          type="text"
          placeholder="Reset code (check your email)"
          className={inputClass}
          value={resetCode}
          onChange={(e) => setResetCode(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="New password"
          className={inputClass}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="Confirm new password"
          className={inputClass}
          value={confirmNewPassword}
          onChange={(e) => setConfirmNewPassword(e.target.value)}
          required
        />
        {error && <p className="text-sm text-red-500">{error}</p>}
        {info && <p className="text-sm text-green-600">{info}</p>}
        <button type="submit" className={buttonClass} disabled={submitting}>
          {submitting ? "Resetting..." : "Reset password"}
        </button>
      </form>
    );
  } else {
    form = (
      <form onSubmit={handleLogin} className="space-y-4 px-4">
        {/* Plain text, not type="email": older accounts sign in with a
            username such as moni.kumari.geniehire, which has no "@". */}
        <input
          type="text"
          placeholder="Email or Username"
          autoComplete="username"
          className={inputClass}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="Password"
          className={inputClass}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {error && <p className="text-sm text-red-500">{error}</p>}
        {info && <p className="text-sm text-green-600">{info}</p>}
        <button type="submit" className={buttonClass} disabled={submitting}>
          {submitting ? "Signing in..." : "Sign in"}
        </button>
        <button
          type="button"
          className="w-full text-sm text-[#667085] hover:text-[#2960FD]"
          onClick={() => showSignIn("forgotRequest")}
        >
          Forgot password?
        </button>
        {/* Only the action is clickable; the question is plain text. */}
        <p className="w-full text-center text-sm text-[#667085]">
          New to GenieHire?{" "}
          <button
            type="button"
            className="text-[#2960FD] font-medium hover:underline"
            onClick={goToSignup}
          >
            Start a free trial
          </button>
        </p>
      </form>
    );
  }

  return <SignInLayout>{form}</SignInLayout>;
}
export default Login;
